import {
  AttributeTranslation,
  ContentAttributeTranslation,
  ContentLevelTranslation,
  ContentSegmentTranslation,
  DayThemeTranslation,
  HighlightTranslation,
  LevelTranslation,
  SegmentTranslation,
  VenueTypeTranslation,
} from '../types/translation';
import { hasOwnProperty, isEmptyFieldValue } from './utils';
import {
  Brand,
  BrandAttribute,
  BrandCommonTextValues,
  BrandContentAttribute,
  BrandContentLevel,
  BrandContentSegment,
  BrandDayTheme,
  BrandEventHighlight,
  BrandFormFieldName,
  BrandFormName,
  BrandLevel,
  BrandSegment,
  BrandVenueType,
  CommonTexts,
  CommonTextsLanguageOptions,
} from '../types/brand';
import { mapBy } from './functional';
import { translateCommonTexts } from './commonTexts';
import {
  DEFAULT_24_7_WORD,
  DEFAULT_AFT_WORD,
  DEFAULT_CLOSED_WORD,
  DEFAULT_DECK_WORD,
  DEFAULT_ENDS_LATE_WORD,
  DEFAULT_FWD_WORD,
  DEFAULT_MID_WORD,
  DEFAULT_PRT_WORD,
  DEFAULT_START_TIME_WORD,
  DEFAULT_STB_WORD,
} from './venue';
import {
  DEFAULT_CONTACT_NAME,
  DEFAULT_DEBARK_DATE,
  DEFAULT_EMBARK_DATE,
  DEFAULT_GROUP_BOOKING,
  DEFAULT_GUEST_EXPERIENCE,
  DEFAULT_GUESTS,
  DEFAULT_TOUR_LEADER,
  DEFAULT_TOUR_LEADER_CABIN,
  DEFAULT_HOSPITALITY_DESK,
  DEFAULT_AGENCY_CONTACT_NAME,
  DEFAULT_SALES_DEPT_EMAIL,
  DEFAULT_SALES_DEPT_NUMBER,
  DEFAULT_PARTY_NAME,
  DEFAULT_GROUP_ID,
  DEFAULT_GROUP_TYPE,
  DEFAULT_CABIN_DROPS,
  DEFAULT_DINING_PREFERENCE,
  DEFAULT_PREPAID_PACKAGES,
  DEFAULT_SALES_OFFICE,
  DEFAULT_BOOKING_NO,
  DEFAULT_GROUP_NOTES,
  DEFAULT_GROUP_FILES,
} from './groupBooking';

export function translateBrandSegment(
  segment: BrandSegment,
  translation?: SegmentTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...segment,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandContentSegment(
  segment: BrandContentSegment,
  translation?: ContentSegmentTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...segment,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandLevel(
  level: BrandLevel,
  translation?: LevelTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...level,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandContentLevel(
  level: BrandContentLevel,
  translation?: ContentLevelTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...level,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandAttribute(
  attribute: BrandAttribute,
  translation?: AttributeTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...attribute,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}
export function translateBrandContentAttribute(
  attribute: BrandContentAttribute,
  translation?: ContentAttributeTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...attribute,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandVenueType(
  venueType: BrandVenueType,
  translation?: VenueTypeTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...venueType,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandDayTheme(
  dayTheme: BrandDayTheme,
  translation?: DayThemeTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...dayTheme,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandHighlight(
  highlight: BrandEventHighlight,
  translation?: HighlightTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...highlight,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export const getCommonTexts = (
  commonTexts: CommonTexts,
  language: CommonTextsLanguageOptions
): BrandCommonTextValues => {
  let texts = commonTexts.default;

  if (language.selected && commonTexts.translations.length) {
    let commonTextTranslationsMap = mapBy(commonTexts.translations, 'refers');

    texts = commonTexts.default.map(
      ct => translateCommonTexts(ct, commonTextTranslationsMap[ct._id], language.selected) || ct
    );
  }

  let [closed] = texts.filter(txt => txt.label === 'venue_closed');
  let [late] = texts.filter(txt => txt.label === 'ends_late');
  let [deck] = texts.filter(txt => txt.label === 'deck');
  let [venue_location_fwd] = texts.filter(txt => txt.label === 'venue_location_fwd');
  let [venue_location_mid] = texts.filter(txt => txt.label === 'venue_location_mid');
  let [venue_location_aft] = texts.filter(txt => txt.label === 'venue_location_aft');
  let [venue_location_prt] = texts.filter(txt => txt.label === 'venue_location_prt');
  let [venue_location_stb] = texts.filter(txt => txt.label === 'venue_location_stb');
  let [twenty_four_seven] = texts.filter(txt => txt.label === '24_7');
  let [over_ride_start_time] = texts.filter(txt => txt.label === 'override_start_time');
  let [tour_leader] = texts.filter(txt => txt.label === 'tour_leader');
  let [tour_leader_cabin] = texts.filter(txt => txt.label === 'tour_leader_cabin');
  let [guest_experience] = texts.filter(txt => txt.label === 'guest_experience');
  let [guests] = texts.filter(txt => txt.label === 'guests');
  let [group_contact_name] = texts.filter(txt => txt.label === 'group_contact_name');
  let [embark_date] = texts.filter(txt => txt.label === 'embark_date');
  let [debark_date] = texts.filter(txt => txt.label === 'debark_date');
  let [group_booking] = texts.filter(txt => txt.label === 'group_booking');
  let [hospitality_desk] = texts.filter(txt => txt.label === 'hospitality_desk');
  let [agency_contact_name] = texts.filter(txt => txt.label === 'agency_contact_name');
  let [sales_dept_email] = texts.filter(txt => txt.label === 'sales_dept_email');
  let [sales_dept_phone] = texts.filter(txt => txt.label === 'sales_dept_phone');
  let [party_name] = texts.filter(txt => txt.label === 'party_name');
  let [group_id] = texts.filter(txt => txt.label === 'group_id');
  let [group_type] = texts.filter(txt => txt.label === 'group_type');
  let [cabin_drops] = texts.filter(txt => txt.label === 'cabin_drops');
  let [dining_preference] = texts.filter(txt => txt.label === 'dining_preference');
  let [prepaid_packages] = texts.filter(txt => txt.label === 'prepaid_packages');
  let [sales_office] = texts.filter(txt => txt.label === 'sales_office');
  let [booking_no] = texts.filter(txt => txt.label === 'booking_no');
  let [group_notes] = texts.filter(txt => txt.label === 'group_notes');
  let [group_files] = texts.filter(txt => txt.label === 'group_files');

  return {
    venue_closed: closed?.name ?? DEFAULT_CLOSED_WORD,
    ends_late: late?.name || DEFAULT_ENDS_LATE_WORD,
    deck: deck?.name ?? DEFAULT_DECK_WORD,
    venue_location_fwd: venue_location_fwd?.name ?? DEFAULT_FWD_WORD,
    venue_location_mid: venue_location_mid?.name ?? DEFAULT_MID_WORD,
    venue_location_aft: venue_location_aft?.name ?? DEFAULT_AFT_WORD,
    venue_location_prt: venue_location_prt?.name ?? DEFAULT_PRT_WORD,
    venue_location_stb: venue_location_stb?.name ?? DEFAULT_STB_WORD,
    twenty_four_seven: twenty_four_seven?.name ?? DEFAULT_24_7_WORD,
    override_start_time: over_ride_start_time?.name ?? DEFAULT_START_TIME_WORD,
    tour_leader: tour_leader?.name ?? DEFAULT_TOUR_LEADER,
    tour_leader_cabin: tour_leader_cabin?.name ?? DEFAULT_TOUR_LEADER_CABIN,
    guest_experience: guest_experience?.name ?? DEFAULT_GUEST_EXPERIENCE,
    guests: guests?.name ?? DEFAULT_GUESTS,
    group_contact_name: group_contact_name?.name ?? DEFAULT_CONTACT_NAME,
    embark_date: embark_date?.name ?? DEFAULT_EMBARK_DATE,
    debark_date: debark_date?.name ?? DEFAULT_DEBARK_DATE,
    group_booking: group_booking?.name ?? DEFAULT_GROUP_BOOKING,
    hospitality_desk: hospitality_desk?.name ?? DEFAULT_HOSPITALITY_DESK,
    agency_contact_name: agency_contact_name?.name ?? DEFAULT_AGENCY_CONTACT_NAME,
    sales_dept_email: sales_dept_email?.name ?? DEFAULT_SALES_DEPT_EMAIL,
    sales_dept_phone: sales_dept_phone?.name ?? DEFAULT_SALES_DEPT_NUMBER,
    party_name: party_name?.name ?? DEFAULT_PARTY_NAME,
    group_id: group_id?.name ?? DEFAULT_GROUP_ID,
    group_type: group_type?.name ?? DEFAULT_GROUP_TYPE,
    cabin_drops: cabin_drops?.name ?? DEFAULT_CABIN_DROPS,
    dining_preference: dining_preference?.name ?? DEFAULT_DINING_PREFERENCE,
    prepaid_packages: prepaid_packages?.name ?? DEFAULT_PREPAID_PACKAGES,
    sales_office: sales_office?.name ?? DEFAULT_SALES_OFFICE,
    booking_no: booking_no?.name ?? DEFAULT_BOOKING_NO,
    group_notes: group_notes?.name ?? DEFAULT_GROUP_NOTES,
    group_files: group_files?.name ?? DEFAULT_GROUP_FILES,
  };
};

export const getMandatoryFormFields = (
  brand: Brand,
  formName: BrandFormName
): Set<BrandFormFieldName> => {
  const fields = brand.formConfig.find(config => config.formName === formName)?.fields ?? [];
  return new Set(fields.filter(field => field.mandatory).map(field => field.fieldName));
};

export function getEmptyMandatoryFields(
  mandatoryFields: Set<BrandFormFieldName>,
  fieldValues: Partial<Record<BrandFormFieldName, unknown>>
): BrandFormFieldName[] {
  return [...mandatoryFields].filter(fieldName => isEmptyFieldValue(fieldValues[fieldName]));
}
